import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Flame, PartyPopper, TrendingUp } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShareButtons } from "@/components/share-buttons";
import { useMyReferralCode } from "@/hooks/useReferral";
import { formatHeat } from "@/lib/behot";
import { useT } from "@/i18n";

/**
 * Celebration shown right after a listing lands on a board: the rank it holds
 * on that board and on the General board, an invite to boost higher, plus a
 * ready-made share message that carries the owner's referral code.
 */
export function ListingPlacedDialog({
  listingName,
  listingSlug,
  shortCode,
  boardName,
  boardSlug,
  rank,
  heatCents,
  generalRank,
  generalHeatCents,
  isOwner = true,
}: {
  listingName: string;
  listingSlug: string;
  shortCode?: string | null;
  boardName: string;
  boardSlug?: string | null;
  rank: number | null;
  heatCents: number;
  generalRank?: number | null;
  generalHeatCents?: number;
  /** False when a fan boosted someone else's listing — thank them instead. */
  isOwner?: boolean;
}) {
  const { t } = useT();
  const refCode = useMyReferralCode();
  const [open, setOpen] = useState(true);

  const path = shortCode ? `/${shortCode}` : `/p/${listingSlug}`;
  const fmt = (r: number | null | undefined) => (r ? `#${String(r).padStart(2, "0")}` : "—");
  const rankLabel = fmt(rank);
  const shareTitle = t("profiles.placedShareMessage", {
    name: listingName,
    rank: rankLabel,
    board: boardName,
    heat: formatHeat(heatCents),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl">
            <PartyPopper className="size-5 shrink-0 text-primary" />
            {isOwner
              ? t("profiles.placedTitle")
              : t("profiles.placedTitleFan", { name: listingName })}
          </DialogTitle>
          <DialogDescription>
            {isOwner
              ? t("profiles.placedSubtitle", { name: listingName, board: boardName })
              : t("profiles.placedSubtitleFan", { name: listingName, board: boardName })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-muted/50 p-4 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {boardName}
            </p>
            <p className="mt-1 font-display text-4xl font-black text-gradient-hot">{rankLabel}</p>
            <p className="mt-1 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <Flame className="size-3.5 text-primary" /> {formatHeat(heatCents)} Heat
            </p>
          </div>
          <div className="rounded-xl border border-border bg-muted/50 p-4 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t("profiles.placedGeneralBoard")}
            </p>
            <p className="mt-1 font-display text-4xl font-black">{fmt(generalRank)}</p>
            <p className="mt-1 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <Flame className="size-3.5 text-primary" />{" "}
              {formatHeat(generalHeatCents ?? heatCents)} Heat
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <TrendingUp className="size-4 text-primary" />
            {t("profiles.placedClimbTitle")}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{t("profiles.placedClimbBody")}</p>
          <Button asChild variant="hot" className="mt-3 w-full">
            <Link
              to="/boost"
              search={{ listing: listingSlug, ...(boardSlug ? { category: boardSlug } : {}) }}
            >
              {t("profiles.placedClimbCta")}
            </Link>
          </Button>
        </div>

        <p className="rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
          {shareTitle}
        </p>

        <ShareButtons path={path} refCode={refCode} title={shareTitle} />

        <Button variant="outline" className="w-full" onClick={() => setOpen(false)}>
          {t("common.close")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
